import type { TypeFetchData, TypeFetchResponseType } from './types';

import { supportsRequestBody } from '../utils';
import { FetchError } from './error';

// ----------------------------------------------------------------------------

function appendQueryParams(url: string, data?: TypeFetchData): string {
    if (!data || typeof data !== 'object' || data instanceof FormData) {
        return url;
    }

    const entries = Object.entries(data)
        .filter(([, v]) => v !== null && v !== undefined)
        .map(([k, v]) => [k, String(v)] as [string, string]);

    const params = new URLSearchParams(entries).toString();
    const separator = url.includes('?') ? '&' : '?';

    return params ? `${url}${separator}${params}` : url;
}

function getRequestBody(
    method: string,
    data: TypeFetchData | undefined,
    headers: Record<string, string>,
): BodyInit | undefined {
    if (!supportsRequestBody(method) || !data) {
        return undefined;
    }

    if (data instanceof FormData || typeof data === 'string') {
        return data;
    }

    if (!headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    return JSON.stringify(data);
}

// ----------------------------------------------------------------------------

function inferResponseType(response: Response): TypeFetchResponseType {
    const ct = response.headers.get('Content-Type') ?? '';

    if (ct.includes('application/json')) {
        return 'json';
    }

    if (ct.startsWith('text/')) {
        return 'text';
    }

    return 'blob';
}

async function readBlobWithProgress(response: Response, onProgress?: (percent: number) => void): Promise<Blob> {
    const reader = response.body?.getReader();

    if (!reader) {
        return response.blob();
    }

    const contentLength = response.headers.get('Content-Length');
    const total = contentLength ? parseInt(contentLength, 10) : 0;

    const chunks: BlobPart[] = [];
    let received = 0;

    while (true) {
        const { done, value } = await reader.read();

        if (done) {
            break;
        }
        if (!value) {
            continue;
        }

        chunks.push(value);
        received += value.length;

        if (total && onProgress) {
            onProgress((received / total) * 100);
        }
    }

    return new Blob(chunks);
}

async function readResponse<T>(
    response: Response,
    responseType: TypeFetchResponseType,
    onProgress?: (percent: number) => void,
): Promise<T> {
    switch (responseType) {
        case 'blob':
            return (onProgress ? await readBlobWithProgress(response, onProgress) : await response.blob()) as T;

        case 'text':
            return (await response.text()) as T;

        case 'arrayBuffer':
            return (await response.arrayBuffer()) as T;

        default:
            return (await response.json()) as T;
    }
}

// ----------------------------------------------------------------------------

async function readErrorResponse(response: Response): Promise<unknown> {
    const rawResponse = await response.text();

    if (!rawResponse.trim()) {
        return null;
    }

    try {
        return JSON.parse(rawResponse);
    } catch {
        return rawResponse;
    }
}

function getErrorMessage(data: unknown, response: Response): string {
    if (typeof data === 'object' && data !== null && 'message' in data && typeof data.message === 'string') {
        return data.message;
    }

    if (typeof data === 'string' && data) {
        return data;
    }

    return response.statusText || `HTTP ${response.status}`;
}

async function createFetchError(response: Response): Promise<FetchError> {
    const data = await readErrorResponse(response);
    const message = getErrorMessage(data, response);

    return new FetchError({
        data,
        message,
        status: response.status,
        statusText: response.statusText,
    });
}

// ----------------------------------------------------------------------------

export { appendQueryParams, createFetchError, getRequestBody, inferResponseType, readResponse };
